import React, { useState, useEffect } from 'react';
import './OpportunityPanel.css';

const OpportunityPanel = ({ properties, opportunityScores }) => {
  const [topOpportunities, setTopOpportunities] = useState([]);
  const [filterCriteria, setFilterCriteria] = useState('all');

  useEffect(() => {
    calculateOpportunities();
  }, [properties, opportunityScores, filterCriteria]);

  const calculateOpportunities = () => {
    if (!properties || properties.length === 0) return;

    let scored = properties.map(property => {
      // Calculate opportunity score based on multiple factors
      let score = 0;
      let factors = [];

      // Vacancy opportunity (high vacancy = opportunity)
      if (property.vacancyPercent > 10) {
        score += 20;
        factors.push('High Vacancy');
      }

      // Age opportunity (older buildings = potential renovation)
      const age = new Date().getFullYear() - property.yearBuilt;
      if (age > 30) {
        score += 15;
        factors.push('Value-Add Potential');
      }

      // Rent opportunity (below market)
      if (property.rentPerSF && property.rentPerSF < 1.5) {
        score += 25;
        factors.push('Below Market Rent');
      }

      // Size opportunity (larger properties)
      if (property.numberOfUnits > 100) {
        score += 20;
        factors.push('Scale Opportunity');
      }

      // Building class opportunity
      if (property.buildingClass === 'B' || property.buildingClass === 'C') {
        score += 20;
        factors.push('Upgrade Potential');
      }

      return {
        ...property,
        opportunityScore: score,
        factors: factors,
        rentGap: calculateRentGap(property, properties),
        capRate: calculateCapRate(property)
      };
    });

    // Sort by opportunity score
    scored.sort((a, b) => b.opportunityScore - a.opportunityScore);
    
    // Filter based on criteria
    if (filterCriteria === 'highCap') {
      scored = scored.filter(p => p.capRate > 7);
    } else if (filterCriteria === 'valueAdd') {
      scored = scored.filter(p => p.factors.includes('Value-Add Potential'));
    } else if (filterCriteria === 'belowMarket') {
      scored = scored.filter(p => p.factors.includes('Below Market Rent'));
    }

    setTopOpportunities(scored.slice(0, 10));
  };

  const calculateRentGap = (property, allProperties) => {
    if (!property.rentPerSF) return 0;
    
    // Find comparable properties
    const comparables = allProperties.filter(p => 
      p.id !== property.id &&
      p.buildingClass === property.buildingClass &&
      p.numberOfUnits && 
      Math.abs(p.numberOfUnits - property.numberOfUnits) < 50 &&
      p.rentPerSF
    );

    if (comparables.length === 0) return 0;

    const avgRent = comparables.reduce((sum, p) => sum + p.rentPerSF, 0) / comparables.length;
    const gap = ((avgRent - property.rentPerSF) / avgRent) * 100;
    
    return gap;
  };

  const calculateCapRate = (property) => {
    if (!property.rentPerSF || !property.rba) return 0;
    
    const annualRent = property.rentPerSF * property.rba;
    const estimatedValue = property.numberOfUnits * 75000; // Rough estimate
    const noi = annualRent * 0.6; // 60% NOI margin estimate
    
    return (noi / estimatedValue) * 100;
  };

  const formatPercent = (value) => {
    if (!value || isNaN(value)) return '—';
    return `${value.toFixed(1)}%`;
  };

  return (
    <div className="opportunity-panel">
      <div className="panel-header">
        <h3 className="panel-title">Opportunity Analysis</h3>
        <select 
          className="filter-select"
          value={filterCriteria}
          onChange={(e) => setFilterCriteria(e.target.value)}
        >
          <option value="all">All Opportunities</option>
          <option value="highCap">High Cap Rate (&gt;7%)</option>
          <option value="valueAdd">Value-Add</option>
          <option value="belowMarket">Below Market Rent</option>
        </select>
      </div>

      <div className="opportunity-list">
        {topOpportunities.length === 0 ? (
          <div className="no-opportunities">
            <p>No opportunities found</p>
            <span>Upload property data to see opportunities</span>
          </div>
        ) : (
          topOpportunities.map((property, index) => (
            <div key={property.id} className="opportunity-card">
              <div className="opportunity-rank">
                {index + 1}
              </div>
              
              <div className="opportunity-content">
                <div className="opportunity-header">
                  <h4>{property.propertyName || property.propertyAddress}</h4>
                  <span className="opportunity-score">
                    Score: {property.opportunityScore}
                  </span>
                </div>
                
                <div className="opportunity-metrics">
                  <div className="metric-item">
                    <span className="metric-label">Units</span>
                    <span className="metric-value">{property.numberOfUnits}</span>
                  </div>
                  <div className="metric-item">
                    <span className="metric-label">Rent Gap</span>
                    <span className={`metric-value ${property.rentGap > 0 ? 'positive' : ''}`}>
                      {formatPercent(property.rentGap)}
                    </span>
                  </div>
                  <div className="metric-item">
                    <span className="metric-label">Cap Rate</span>
                    <span className="metric-value">{formatPercent(property.capRate)}</span>
                  </div>
                </div>

                <div className="opportunity-factors">
                  {property.factors.map(factor => (
                    <span key={factor} className="factor-tag">{factor}</span>
                  ))}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default OpportunityPanel;